import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import PermissionGuard from '../../components/permissions/PermissionGuard'
import {
  HiOutlineArrowLeft,
  HiOutlineVideoCamera,
  HiOutlineUser,
  HiOutlineCalendarDays,
  HiOutlineDocumentText,
  HiOutlinePencilSquare,
  HiOutlineTrash,
  HiOutlineEye,
  HiOutlineFolder,
  HiOutlineChatBubbleLeft,
} from 'react-icons/hi2'
import DocumentUploadModal from '../../components/documents/DocumentUploadModal'
import DocumentTable from '../../components/documents/DocumentTable'
import MessagePanel from '../../components/messages/MessagePanel'
import BlockedAction from '../../components/common/BlockedAction'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  IconButton,
  PageHeader,
  Spinner,
} from '../../components/ui'

const QUICK_TONE = {
  brand:   'bg-brand-soft text-brand',
  info:    'bg-info-soft text-info',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  neutral: 'bg-muted text-ink-secondary',
}

export default function SessionDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError, showConfirm } = useAlerts()
  const { getEntity } = useWorkspace()

  const [sessionData, setSessionData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showMessagePanel, setShowMessagePanel] = useState(false)

  useEffect(() => {
    fetchSessionDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ownerAccountId])

  async function fetchSessionDetails() {
    try {
      setLoading(true)
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.get(`/sessions/${id}/`, { params })
      if (response.success) {
        setSessionData(response.data.data || response.data)
      } else {
        showError(t('cases.loadFailed', { entity: getEntity('session').singular.toLowerCase() }))
        navigate('/sessions')
      }
    } catch {
      showError(t('cases.loadFailed', { entity: getEntity('session').singular.toLowerCase() }))
      navigate('/sessions')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteSession = async () => {
    const confirmed = await showConfirm(
      t('sessions.deleteTitle', { entity: getEntity('session').singular }),
      t('sessions.deleteMessage', {
        title: sessionData?.title,
        documents: getEntity('document').plural.toLowerCase(),
      }),
      t('common.delete'),
      t('common.cancel'),
      'destructive',
    )
    if (!confirmed) return
    try {
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.delete(`/sessions/${id}/`, { params })
      if (response.success) {
        showSuccess(t('cases.deleteSuccess', { entity: getEntity('session').singular }))
        navigate('/sessions')
      } else {
        showError(t('cases.deleteFailed', { entity: getEntity('session').singular.toLowerCase() }))
      }
    } catch {
      showError(t('cases.deleteFailed', { entity: getEntity('session').singular.toLowerCase() }))
    }
  }

  const formatDate = (s) => s
    ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'N/A'

  const formatDateTime = (s) => s
    ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'N/A'

  const tabs = [
    { id: 'overview',  label: t('sessions.detail.tabOverview'),                                          icon: HiOutlineEye,          count: null },
    { id: 'documents', label: t('sessions.detail.tabDocuments', { entity: getEntity('document').plural }), icon: HiOutlineDocumentText, count: sessionData?.documents_count || 0 },
  ]

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-muted rounded-xl animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="w-48 h-7 bg-muted rounded animate-pulse" />
            <div className="w-96 h-4 bg-muted rounded animate-pulse" />
          </div>
        </div>
        <Card padding="md"><div className="h-64 bg-muted rounded-lg animate-pulse" /></Card>
        <div className="flex justify-center"><Spinner /></div>
      </div>
    )
  }

  if (!sessionData) {
    return (
      <Card padding="lg">
        <EmptyState
          icon={HiOutlineVideoCamera}
          title={t('sessions.detail.notFound', { entity: getEntity('session').singular })}
          description={t('sessions.detail.notFoundDescription', { entity: getEntity('session').singular.toLowerCase() })}
          action={
            <Button leadingIcon={HiOutlineArrowLeft} onClick={() => navigate('/sessions')}>
              {t('sessions.detail.backTo', { entity: getEntity('session').plural })}
            </Button>
          }
        />
      </Card>
    )
  }

  const caseId = sessionData.case_id || sessionData.case?.id

  return (
    <div className={`space-y-6 transition-all duration-300 ${showMessagePanel ? 'me-0 sm:me-[400px] md:me-[420px] lg:me-[450px] xl:me-[480px]' : ''}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <IconButton
            icon={HiOutlineArrowLeft}
            aria-label={t('sessions.detail.backTo', { entity: getEntity('session').plural })}
            variant="ghost"
            onClick={() => navigate('/sessions')}
            className="rtl:rotate-180"
          />
          <span className="w-12 h-12 bg-brand-soft text-brand rounded-xl flex items-center justify-center flex-shrink-0">
            <HiOutlineVideoCamera className="w-6 h-6" />
          </span>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-ink-primary truncate">{sessionData.title}</h1>
            <p className="text-sm text-ink-muted">
              {isWorkspaceSwitched
                ? t('sessions.detail.subtitleSwitched', { entity: getEntity('session').singular, name: user?.switchedAccountName })
                : t('sessions.detail.subtitle', { datetime: formatDateTime(sessionData.date) })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={showMessagePanel ? 'primary' : 'secondary'}
            leadingIcon={HiOutlineChatBubbleLeft}
            onClick={() => setShowMessagePanel(v => !v)}
          >
            {t('nav.messages')}
          </Button>
          <PermissionGuard resource="session" action="update">
            <Button variant="secondary" leadingIcon={HiOutlinePencilSquare} onClick={() => navigate(`/sessions/${id}/edit`)}>
              {t('sessions.detail.editSession', { entity: getEntity('session').singular })}
            </Button>
          </PermissionGuard>
          <PermissionGuard resource="session" action="delete">
            <Button
              variant="secondary"
              leadingIcon={HiOutlineTrash}
              onClick={handleDeleteSession}
              className="!text-danger hover:!bg-danger-soft hover:!border-danger/40"
            >
              {t('common.delete')}
            </Button>
          </PermissionGuard>
        </div>
      </div>

      <Card padding="none">
        <nav className="flex gap-1 px-3 sm:px-5 border-b border-border-subtle overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const active = activeTab === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-3 -mb-px border-b-2 font-medium text-sm inline-flex items-center gap-2 whitespace-nowrap transition-colors ${
                  active
                    ? 'border-brand text-brand'
                    : 'border-transparent text-ink-secondary hover:text-ink-primary hover:border-border-strong'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== null && (
                  <Badge tone={active ? 'brand' : 'neutral'} size="sm">{tab.count}</Badge>
                )}
              </button>
            )
          })}
        </nav>

        <div className="p-4 sm:p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field icon={HiOutlineVideoCamera} label={t('sessions.detail.fieldTitle', { entity: getEntity('session').singular })}>{sessionData.title}</Field>
                <Field icon={HiOutlineFolder} label={t('sessions.detail.fieldCase', { caseEntity: getEntity('case').singular })}>
                  {sessionData.case_title || sessionData.case?.title || 'N/A'}
                </Field>
                <Field icon={HiOutlineUser} label={t('sessions.detail.fieldClient', { clientEntity: getEntity('client').singular })}>
                  {sessionData.client_name || sessionData.client?.name || sessionData.case?.client?.name || 'N/A'}
                </Field>
                <Field icon={HiOutlineCalendarDays} label={t('sessions.detail.fieldDateTime')}>{formatDateTime(sessionData.date)}</Field>
              </div>

              {sessionData.description && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-ink-muted mb-1.5 inline-flex items-center gap-2">
                    <HiOutlineDocumentText className="w-4 h-4" />
                    {t('sessions.detail.fieldDescription')}
                  </div>
                  <Card variant="bordered" padding="md">
                    <p className="text-sm text-ink-primary whitespace-pre-wrap">{sessionData.description}</p>
                  </Card>
                </div>
              )}

              <Card padding="md">
                <Card.Header title={t('sessions.detail.quickActions')} />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <PermissionGuard resource="document" action="create">
                    <BlockedAction action="upload_document">
                      <QuickAction
                        icon={HiOutlineDocumentText}
                        tone="brand"
                        title={t('sessions.detail.addDocument', { entity: getEntity('document').singular })}
                        description={t('sessions.detail.addDocumentDesc', { sessionEntity: getEntity('session').singular.toLowerCase() })}
                        onClick={() => setShowUploadModal(true)}
                        fullWidth
                      />
                    </BlockedAction>
                  </PermissionGuard>
                  <PermissionGuard resource="session" action="update">
                    <QuickAction
                      icon={HiOutlinePencilSquare}
                      tone="neutral"
                      title={t('sessions.detail.editSession', { entity: getEntity('session').singular })}
                      description={t('sessions.detail.editSessionDesc', { entity: getEntity('session').singular.toLowerCase() })}
                      onClick={() => navigate(`/sessions/${id}/edit`)}
                    />
                  </PermissionGuard>
                  {caseId && (
                    <QuickAction
                      icon={HiOutlineFolder}
                      tone="info"
                      title={t('sessions.detail.viewCase', { entity: getEntity('case').singular })}
                      description={t('sessions.detail.viewCaseDesc', { caseEntity: getEntity('case').singular.toLowerCase() })}
                      onClick={() => navigate(`/cases/${caseId}`)}
                    />
                  )}
                </div>
              </Card>

              <Card padding="md">
                <Card.Header title={t('sessions.detail.statistics', { entity: getEntity('session').singular })} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Stat icon={HiOutlineDocumentText} label={t('sessions.detail.documentsLabel', { entity: getEntity('document').plural })} value={sessionData?.documents_count || 0} />
                  <Stat icon={HiOutlineCalendarDays} label={t('sessions.detail.sessionDate', { entity: getEntity('session').singular })} value={formatDate(sessionData.date)} />
                </div>
              </Card>
            </div>
          )}

          {activeTab === 'documents' && (
            <DocumentTable
              context="session"
              contextId={parseInt(id)}
              contextName={sessionData?.title}
              onDocumentUpdate={fetchSessionDetails}
              uploadModalProps={{
                clientId: sessionData?.client_id || sessionData?.case?.client_id,
                caseId:   sessionData?.case_id || sessionData?.case?.id,
                sessionId: parseInt(id),
              }}
            />
          )}
        </div>
      </Card>

      <MessagePanel
        isOpen={showMessagePanel}
        onClose={() => setShowMessagePanel(false)}
        entityType="session"
        entityId={parseInt(id)}
        entityName={sessionData?.title}
      />

      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={() => {
          fetchSessionDetails()
          setShowUploadModal(false)
        }}
        preselectedClient={sessionData?.client_id || sessionData?.client?.id || sessionData?.case?.client?.id}
        preselectedCase={sessionData?.case_id || sessionData?.case?.id}
        preselectedSession={id}
      />
    </div>
  )
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wider text-ink-muted mb-1.5 inline-flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <p className="text-sm text-ink-primary font-medium">{children}</p>
    </div>
  )
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className="w-4 h-4 text-ink-muted flex-shrink-0" />}
        <span className="text-sm text-ink-secondary truncate">{label}</span>
      </div>
      <span className="text-sm font-semibold text-ink-primary text-end">{value}</span>
    </div>
  )
}

function QuickAction({ icon: Icon, tone = 'brand', title, description, onClick, fullWidth = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-start p-3 rounded-xl border border-border bg-surface hover:border-border-strong hover:shadow-token-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 group ${fullWidth ? 'w-full' : ''}`}
    >
      <div className="flex items-start gap-3">
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105 ${QUICK_TONE[tone] || QUICK_TONE.brand}`}>
          {Icon && <Icon className="w-4 h-4" />}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink-primary">{title}</p>
          <p className="text-xs text-ink-muted mt-0.5 line-clamp-2">{description}</p>
        </div>
      </div>
    </button>
  )
}
